/**
 * render/nameplate_ui.ts — Entity Nameplates, Target Frame, and Status Display
 *
 * Every entity in the world gets a nameplate: colored name text above their head.
 * Creatures get red, NPCs get green, players get color based on PvP status.
 * The target frame shows detailed info about the currently selected entity.
 * Ghost overlay renders the death/spirit UI.
 */

import type { GameState } from "../engine/state.js";
import type { Camera } from "../iso/projection.js";
import { getCreatureHp, isTrainingDummy } from "../combat/system.js";
import { drawDummyNameplate } from "./quest_ui.js";

// ─── Palette ────────────────────────────────────────────────────────────────

const C = {
  woodDark:   "#1e1508",
  woodMid:    "#2a1d0e",
  goldDark:   "#6b4f0c",
  goldMid:    "#9b7318",
  goldBright: "#d4a030",
  goldGlow:   "#f0c860",
  textPrime:  "#f0d88a",
  textDim:    "#c0a060",
  hpBg:       "#0e0a04",
  hpBorder:   "#3a2810",
} as const;

// ─── Disposition Colors ─────────────────────────────────────────────────────
// These match WoW conventions: green=friendly, yellow=neutral, red=hostile,
// orange=PvP flagged, deep red=aggressor

const DISPOSITION_COLORS: Record<string, string> = {
  friendly:       "#60c848",    // bright green — safe, can't attack
  neutral:        "#f0e060",    // yellow — attackable but won't aggro
  hostile:        "#e04040",    // red — will attack on sight
  attackable:     "#f0c040",    // yellow-gold — can be attacked
  pvp_flagged:    "#e88020",    // orange — PvP enabled, mutual combat
  pvp_aggressor:  "#cc2020",    // deep red — attacked someone, free kill
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function toScreen(
  tileX: number, tileY: number, elevation: number,
  camera: Camera, _cw: number, _ch: number,
): { x: number; y: number } {
  const wx = tileX + 0.5;
  const wy = tileY + 0.5;
  const sx = (wx - wy) * 32;
  const sy = (wx + wy) * 16 - elevation * 16;
  // Must match applyCamera convention: camera.x/y already include canvas center offset
  return {
    x: sx * camera.zoom + camera.x,
    y: sy * camera.zoom + camera.y,
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function hpColor(ratio: number): string {
  const r = ratio > 0.5
    ? Math.round(lerp(200, 74, (ratio - 0.5) * 2))
    : Math.round(lerp(154, 200, ratio * 2));
  const g = ratio > 0.5
    ? Math.round(lerp(160, 154, (ratio - 0.5) * 2))
    : Math.round(lerp(32, 160, ratio * 2));
  const b = 32;
  return `rgb(${r},${g},${b})`;
}

// ─── Creature Nameplates ────────────────────────────────────────────────────

function drawCreatureNameplate(
  ctx: CanvasRenderingContext2D,
  name: string,
  hp: number,
  maxHp: number,
  sx: number,    // screen X (already projected)
  sy: number,    // screen Y (already projected)
  zoom: number,
  isTarget: boolean,
  disposition: string,
): void {
  const nameColor = DISPOSITION_COLORS[disposition] ?? DISPOSITION_COLORS.hostile;
  const barW = 52;
  const barH = 4;
  const barY = sy - 48 * zoom;
  const nameY = barY - 12;

  // Name text
  ctx.save();
  ctx.font = "bold 9px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";

  // Shadow for readability
  ctx.shadowColor = "rgba(0,0,0,0.8)";
  ctx.shadowBlur = 3;

  ctx.fillStyle = nameColor;
  ctx.fillText(name, sx, nameY);
  ctx.shadowBlur = 0;

  // Target selection ring — golden glow around nameplate when selected
  if (isTarget) {
    const tw = ctx.measureText(name).width;
    ctx.strokeStyle = C.goldBright;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.5 + Math.sin(performance.now() / 200) * 0.2;
    roundRect(ctx, sx - tw / 2 - 4, nameY - 10, tw + 8, 12, 3);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // HP bar (only for combat entities with HP)
  if (maxHp > 0 && hp > 0) {
    const bx = sx - barW / 2;

    // Background
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(bx - 1, barY - 1, barW + 2, barH + 2);
    ctx.fillStyle = C.hpBg;
    ctx.fillRect(bx, barY, barW, barH);

    // Fill
    const ratio = clamp(hp / maxHp, 0, 1);
    const fillW = Math.round(barW * ratio);
    if (fillW > 0) {
      ctx.fillStyle = hpColor(ratio);
      ctx.fillRect(bx, barY, fillW, barH);

      // Specular
      const specG = ctx.createLinearGradient(bx, barY, bx, barY + barH * 0.5);
      specG.addColorStop(0, "rgba(255,255,255,0.08)");
      specG.addColorStop(1, "transparent");
      ctx.fillStyle = specG;
      ctx.fillRect(bx, barY, fillW, barH * 0.5);
    }

    // Border — threat-colored: red = chasing you, default = dark
    ctx.strokeStyle = "#0a0604";
    ctx.lineWidth = 0.5;
    ctx.strokeRect(bx, barY, barW, barH);

    // ── Enrage glow — pulsing red border when creature < 20% HP ──
    if (ratio > 0 && ratio < 0.20) {
      const enragePulse = 0.5 + 0.5 * Math.sin(performance.now() / 1000 * 6);
      ctx.save();
      ctx.shadowColor = "#ff4020";
      ctx.shadowBlur = 4 + enragePulse * 6;
      ctx.strokeStyle = `rgba(255, 64, 32, ${0.5 + enragePulse * 0.5})`;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(bx - 2, barY - 2, barW + 4, barH + 4);
      ctx.restore();
    }
  }

  ctx.restore();
}

// ─── Threat Nameplate Extension ──────────────────────────────────────────────
// Draw a threat glow under the creature nameplate when it's in combat with player

function drawNameplateThreatGlow(
  ctx: CanvasRenderingContext2D,
  sx: number, barY: number, barW: number,
  isChasing: boolean,
): void {
  if (!isChasing) return;
  const bx = sx - barW / 2;
  ctx.save();
  ctx.strokeStyle = "#e04040";
  ctx.lineWidth = 1.5;
  ctx.shadowColor = "#e04040";
  ctx.shadowBlur = 4;
  ctx.strokeRect(bx - 1, barY - 1, barW + 2, 6);
  ctx.restore();
}

// ─── Nameplate Raid Marker ──────────────────────────────────────────────────
// Draw a raid marker icon above the creature/player nameplate

function drawNameplateMarker(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number, zoom: number,
  marker: string | null,
): void {
  if (!marker) return;
  const MARKER_GLYPHS: Record<string, string> = {
    skull: "\u2620", cross: "\u2720", star: "\u2605", diamond: "\u2666",
    moon: "\u263D", triangle: "\u25B2", circle: "\u25CF", square: "\u25A0",
  };
  const MARKER_COLORS: Record<string, string> = {
    skull: "#e0e0e0", cross: "#e04040", star: "#f0c860", diamond: "#a050d0",
    moon: "#88bbee", triangle: "#44cc44", circle: "#e88020", square: "#4888d8",
  };
  const glyph = MARKER_GLYPHS[marker];
  if (!glyph) return;

  const my = sy - 60 * zoom;
  ctx.save();
  ctx.font = "bold 14px serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.7)";
  ctx.shadowBlur = 3;
  ctx.fillStyle = MARKER_COLORS[marker] ?? "#f0c860";
  ctx.fillText(glyph, sx, my);
  ctx.restore();
}

// ─── NPC Nameplates ─────────────────────────────────────────────────────────

function drawNpcNameplate(
  ctx: CanvasRenderingContext2D,
  name: string,
  title: string,
  sx: number,
  sy: number,
  zoom: number,
  isTarget: boolean,
): void {
  const nameY = sy - 48 * zoom;

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.shadowColor = "rgba(0,0,0,0.8)";
  ctx.shadowBlur = 3;

  // Name in green
  ctx.font = "bold 9px monospace";
  ctx.fillStyle = DISPOSITION_COLORS.friendly;
  ctx.fillText(name, sx, nameY);

  // Title below name in dim gold
  if (title) {
    ctx.font = "8px monospace";
    ctx.fillStyle = C.textDim;
    ctx.fillText(`<${title}>`, sx, nameY + 10);
  }

  // Target ring
  if (isTarget) {
    const tw = ctx.measureText(name).width;
    ctx.shadowBlur = 0;
    ctx.strokeStyle = C.goldBright;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.5 + Math.sin(performance.now() / 200) * 0.2;
    roundRect(ctx, sx - tw / 2 - 4, nameY - 10, tw + 8, 12, 3);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

// ─── Player Nameplates ──────────────────────────────────────────────────────

function drawPlayerNameplate(
  ctx: CanvasRenderingContext2D,
  name: string,
  level: number,
  disposition: string,
  sx: number,
  sy: number,
  zoom: number,
  isTarget: boolean,
  isDead: boolean,
  pvpFlagged: boolean,
): void {
  const nameColor = DISPOSITION_COLORS[disposition] ?? DISPOSITION_COLORS.friendly;
  const nameY = sy - 52 * zoom;

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.shadowColor = "rgba(0,0,0,0.8)";
  ctx.shadowBlur = 3;

  // Ghost state — dim and italic
  if (isDead) {
    ctx.globalAlpha = 0.4;
    ctx.font = "italic 9px monospace";
    ctx.fillStyle = "#a0a0a0";
    ctx.fillText(`[Ghost] ${name}`, sx, nameY);
    ctx.restore();
    return;
  }

  // Name with level
  ctx.font = "bold 9px monospace";
  const displayText = `[${level}] ${name}`;
  ctx.fillStyle = nameColor;
  ctx.fillText(displayText, sx, nameY);

  // PvP flag icon
  if (pvpFlagged) {
    ctx.font = "8px monospace";
    ctx.fillStyle = "#e88020";
    const tw = ctx.measureText(displayText).width;
    ctx.fillText("\u2694", sx + tw / 2 + 6, nameY); // crossed swords
  }

  // Target ring
  if (isTarget) {
    ctx.shadowBlur = 0;
    const tw = ctx.measureText(displayText).width;
    ctx.strokeStyle = C.goldBright;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.5 + Math.sin(performance.now() / 200) * 0.2;
    roundRect(ctx, sx - tw / 2 - 4, nameY - 10, tw + 8, 12, 3);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

// ─── Target Frame ───────────────────────────────────────────────────────────
// A WoW-style portrait/info panel in the top-center of the screen showing
// the selected entity's name, level, HP bar, and disposition.

const TF_W = 220;
const TF_H = 68;
const TF_R = 6;
const TF_HP_H = 10;

function drawTargetFrame(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const target = state.player.target;
  if (!target.id) return;

  const cw = state.canvasWidth;

  // Position: top-center, below any compass
  const fx = (cw - TF_W) / 2;
  const fy = 8;

  // Entrance animation
  const age = now - target.selectedAt;
  const slideIn = Math.min(1, age * 5); // 0.2s slide
  const alpha = clamp(slideIn, 0, 1);
  const offsetY = (1 - slideIn) * -15;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, offsetY);

  // Panel background
  roundRect(ctx, fx, fy, TF_W, TF_H, TF_R);
  ctx.fillStyle = C.woodDark;
  ctx.fill();

  // Inner highlight
  roundRect(ctx, fx, fy, TF_W, TF_H, TF_R);
  ctx.save();
  ctx.clip();
  const innerG = ctx.createLinearGradient(fx, fy, fx, fy + 8);
  innerG.addColorStop(0, "rgba(52,36,20,0.6)");
  innerG.addColorStop(1, "transparent");
  ctx.fillStyle = innerG;
  ctx.fillRect(fx, fy, TF_W, 8);
  ctx.restore();

  // Gold border
  roundRect(ctx, fx, fy, TF_W, TF_H, TF_R);
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Disposition color accent line on left edge
  const dispColor = DISPOSITION_COLORS[target.disposition] ?? DISPOSITION_COLORS.neutral;
  ctx.fillStyle = dispColor;
  roundRect(ctx, fx, fy, 4, TF_H, TF_R);
  ctx.fill();

  // ── Name and level ──────────────────────────────────────────────────
  ctx.save();
  ctx.font = "bold 11px monospace";
  ctx.fillStyle = dispColor;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  // Elite/rare/boss badge — golden crown or silver diamond before name
  let namePrefix = "";
  if (target.eliteRank === "boss") namePrefix = "\ud83d\udc51 "; // crown
  else if (target.eliteRank === "rare_elite") namePrefix = "\u2726 "; // 4-point star
  else if (target.eliteRank === "rare") namePrefix = "\u2726 ";
  else if (target.eliteRank === "elite") namePrefix = "\u2605 "; // star

  // Elite border glow
  if (target.eliteRank !== "normal") {
    const eliteColor = target.eliteRank === "boss" ? "#ff4040"
      : target.eliteRank === "rare_elite" ? "#c0c0ff"
      : target.eliteRank === "rare" ? "#c0c0ff"
      : "#d4a030";
    roundRect(ctx, fx - 1, fy - 1, TF_W + 2, TF_H + 2, TF_R + 1);
    ctx.strokeStyle = eliteColor;
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  const nameText = target.level > 0
    ? `${namePrefix}[${target.level}] ${target.name}`
    : namePrefix + (target.name ?? "Unknown");
  ctx.fillText(nameText, fx + 12, fy + 6);

  // Type label (right-aligned)
  ctx.font = "9px monospace";
  ctx.fillStyle = C.textDim;
  ctx.textAlign = "right";
  const typeLabel = target.type === "creature" ? "Creature"
    : target.type === "npc" ? "NPC"
    : target.type === "player" ? "Player"
    : "Entity";
  ctx.fillText(typeLabel, fx + TF_W - 10, fy + 8);

  // Target marker icon (if set)
  if (target.marker) {
    const MARKER_ICONS: Record<string, string> = {
      skull: "\ud83d\udc80", cross: "\u2716", star: "\u2b50", diamond: "\ud83d\udd37",
      moon: "\ud83c\udf19", triangle: "\u25b2", circle: "\u2b55", square: "\u25a0",
    };
    ctx.font = "11px monospace";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "right";
    ctx.fillText(MARKER_ICONS[target.marker] ?? "", fx + TF_W - 10, fy + 18);
  }
  ctx.restore();

  // ── HP bar (only for entities with HP) ─────────────────────────────
  if (target.maxHp > 0) {
    const bx = fx + 12;
    const by = fy + 22;
    const bw = TF_W - 24;

    // Background
    roundRect(ctx, bx, by, bw, TF_HP_H, 3);
    ctx.fillStyle = C.hpBg;
    ctx.fill();

    // Fill
    const ratio = clamp(target.hp / target.maxHp, 0, 1);
    const fillW = Math.round(bw * ratio);

    if (fillW > 0) {
      ctx.save();
      roundRect(ctx, bx, by, bw, TF_HP_H, 3);
      ctx.clip();

      const fillG = ctx.createLinearGradient(bx, by, bx + bw, by);
      fillG.addColorStop(0, hpColor(ratio));
      fillG.addColorStop(1, hpColor(Math.max(0, ratio - 0.15)));
      ctx.fillStyle = fillG;
      ctx.fillRect(bx, by, fillW, TF_HP_H);

      // Specular
      const specG = ctx.createLinearGradient(bx, by, bx, by + TF_HP_H * 0.5);
      specG.addColorStop(0, "rgba(255,255,255,0.1)");
      specG.addColorStop(1, "transparent");
      ctx.fillStyle = specG;
      ctx.fillRect(bx, by, fillW, TF_HP_H * 0.5);

      ctx.restore();
    }

    // Border
    roundRect(ctx, bx, by, bw, TF_HP_H, 3);
    ctx.strokeStyle = C.hpBorder;
    ctx.lineWidth = 1;
    ctx.stroke();

    // HP text
    ctx.save();
    ctx.font = "bold 8px monospace";
    ctx.fillStyle = C.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const hpPct = target.maxHp > 0 ? Math.round(target.hp / target.maxHp * 100) : 0;
    ctx.fillText(`${target.hp} / ${target.maxHp}  (${hpPct}%)`, bx + bw / 2, by + TF_HP_H / 2);
    ctx.restore();

    // ── Execute range indicator — pulsing red glow when target < 20% HP ──
    if (ratio > 0 && ratio < 0.20) {
      const pulse = 0.5 + 0.5 * Math.sin(now * 8);
      ctx.save();
      ctx.shadowColor = "#ff2020";
      ctx.shadowBlur = 4 + pulse * 6;
      roundRect(ctx, bx - 1, by - 1, bw + 2, TF_HP_H + 2, 4);
      ctx.strokeStyle = `rgba(255, 32, 32, ${0.4 + pulse * 0.4})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      // "EXECUTE" text overlay on the HP bar
      ctx.save();
      ctx.font = "bold 7px monospace";
      ctx.fillStyle = `rgba(255, 100, 100, ${0.6 + pulse * 0.4})`;
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText("EXECUTE", bx + bw - 4, by + TF_HP_H / 2);
      ctx.restore();
    }

    // ── Enrage indicator — creature below 20% HP shows red pulsing border + label ──
    if (ratio > 0 && ratio < 0.20 && target.type === "creature") {
      const enragePulse = 0.5 + 0.5 * Math.sin(now * 6);
      ctx.save();
      ctx.shadowColor = "#ff4020";
      ctx.shadowBlur = 6 + enragePulse * 8;
      roundRect(ctx, fx - 2, fy - 2, TF_W + 4, TF_H + 4, TF_R + 2);
      ctx.strokeStyle = `rgba(255, 64, 32, ${0.3 + enragePulse * 0.5})`;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();

      // "ENRAGED" label above the frame
      ctx.save();
      ctx.font = "bold 9px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillStyle = `rgba(255, 80, 40, ${0.7 + enragePulse * 0.3})`;
      ctx.shadowColor = "#ff4020";
      ctx.shadowBlur = 4;
      ctx.fillText("ENRAGED", fx + TF_W / 2, fy - 3);
      ctx.restore();
    }
  }

  // ── Elite rank frame decoration ──────────────────────────────────
  if (target.eliteRank && target.eliteRank !== "normal") {
    const eliteColors: Record<string, string> = {
      elite: "#d4a030",       // gold
      rare: "#4888d8",        // blue
      rare_elite: "#a050d0",  // purple
      boss: "#e04040",        // red
    };
    const eliteGlow = eliteColors[target.eliteRank] ?? C.goldDark;
    ctx.save();
    ctx.strokeStyle = eliteGlow;
    ctx.lineWidth = 2;
    ctx.shadowColor = eliteGlow;
    ctx.shadowBlur = 6;
    roundRect(ctx, fx - 1, fy - 1, TF_W + 2, TF_H + 2, TF_R + 1);
    ctx.stroke();
    ctx.restore();
  }

  // ── Raid marker icon (top-right of frame) ─────────────────────────
  if (target.marker) {
    const markerGlyphs: Record<string, string> = {
      skull: "\u2620", cross: "\u2720", star: "\u2605", diamond: "\u2666",
      moon: "\u263D", triangle: "\u25B2", circle: "\u25CF", square: "\u25A0",
    };
    const markerColors: Record<string, string> = {
      skull: "#e0e0e0", cross: "#e04040", star: "#f0c860", diamond: "#a050d0",
      moon: "#88bbee", triangle: "#44cc44", circle: "#e88020", square: "#4888d8",
    };
    const glyph = markerGlyphs[target.marker] ?? "\u25CF";
    ctx.save();
    ctx.font = "14px serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0,0,0,0.7)";
    ctx.shadowBlur = 3;
    ctx.fillStyle = markerColors[target.marker] ?? "#f0c860";
    ctx.fillText(glyph, fx + TF_W - 22, fy + 10);
    ctx.restore();
  }

  // ── Distance indicator ─────────────────────────────────────────────
  ctx.save();
  ctx.font = "8px monospace";
  ctx.fillStyle = C.textDim;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(`${target.distance.toFixed(1)} tiles`, fx + 12, fy + 36);
  ctx.restore();

  // ── Disposition text ───────────────────────────────────────────────
  ctx.save();
  ctx.font = "8px monospace";
  ctx.fillStyle = dispColor;
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  const dispLabel = target.disposition === "pvp_aggressor" ? "Aggressor"
    : target.disposition === "pvp_flagged" ? "PvP"
    : target.disposition === "hostile" ? "Hostile"
    : target.disposition === "attackable" ? "Attackable"
    : target.disposition === "neutral" ? "Neutral"
    : "Friendly";
  ctx.fillText(dispLabel, fx + TF_W - 10, fy + 36);
  ctx.restore();

  // ── Cast bar ────────────────────────────────────────────────────────
  if (target.castBar) {
    const cbx = fx + 12;
    const cby = fy + TF_H - 14;
    const cbw = TF_W - 24;
    const cbh = 8;

    // Background
    roundRect(ctx, cbx, cby, cbw, cbh, 2);
    ctx.fillStyle = "#0e0a04";
    ctx.fill();

    // Fill
    const cbRatio = clamp(target.castBar.progress / Math.max(0.01, target.castBar.duration), 0, 1);
    const cbFillW = Math.round(cbw * cbRatio);
    if (cbFillW > 0) {
      ctx.save();
      roundRect(ctx, cbx, cby, cbw, cbh, 2);
      ctx.clip();
      const cbG = ctx.createLinearGradient(cbx, cby, cbx + cbw, cby);
      cbG.addColorStop(0, "#c8a020");
      cbG.addColorStop(1, "#e8c040");
      ctx.fillStyle = cbG;
      ctx.fillRect(cbx, cby, cbFillW, cbh);
      ctx.restore();
    }

    // Border
    roundRect(ctx, cbx, cby, cbw, cbh, 2);
    ctx.strokeStyle = C.hpBorder;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Cast name
    ctx.save();
    ctx.font = "bold 7px monospace";
    ctx.fillStyle = C.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(target.castBar.name, cbx + cbw / 2, cby + cbh / 2);
    ctx.restore();
  }

  // ── Buff/debuff icons row ──────────────────────────────────────────
  // Show active buffs on the target (if creature, pull from state)
  if (target.type === "creature" && target.id) {
    const creature = state.creatures.find(c => c.id === target.id);
    if (creature && (creature as any).buffs) {
      const buffs = (creature as any).buffs as Array<{ icon: string; color?: string }>;
      const iconY = fy + 47;
      for (let i = 0; i < Math.min(buffs.length, 8); i++) {
        const bx = fx + 12 + i * 14;
        ctx.save();
        ctx.font = "10px monospace";
        ctx.fillStyle = buffs[i].color ?? "#b0a080";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(buffs[i].icon, bx + 6, iconY + 5);
        ctx.restore();
      }
    }
  }

  // ── Target-of-target ───────────────────────────────────────────────
  // Show who/what the target is currently attacking (if creature)
  if (target.type === "creature" && target.id) {
    const creature = state.creatures.find(c => c.id === target.id);
    if (creature && creature.behaviorState.current === "chase") {
      // Creature is chasing the player
      ctx.save();
      ctx.font = "8px monospace";
      ctx.fillStyle = "#e06040";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("\u25B6 You", fx + 12, fy + 48);
      ctx.restore();
    }
  }

  ctx.restore(); // global alpha + translation
}

// ─── PvP Status Badge ───────────────────────────────────────────────────────
// Small indicator near the minimap/compass showing the player's PvP status

function drawPvPBadge(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const pvp = state.player.pvp;
  if (!pvp.flagged) return;

  const cw = state.canvasWidth;
  const bx = cw - 90;
  const by = 60;
  const bw = 78;
  const bh = 20;

  // Pulse if aggressor
  let pulse = 1;
  if (pvp.aggressor) {
    pulse = 0.7 + Math.sin(now * 4) * 0.3;
  }

  ctx.save();
  ctx.globalAlpha = pulse * 0.9;

  // Background pill
  roundRect(ctx, bx, by, bw, bh, 4);
  ctx.fillStyle = C.woodDark;
  ctx.fill();

  // Border — orange for flagged, red for aggressor
  roundRect(ctx, bx, by, bw, bh, 4);
  ctx.strokeStyle = pvp.aggressor ? "#cc2020" : "#e88020";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Text
  ctx.font = "bold 9px monospace";
  ctx.fillStyle = pvp.aggressor ? "#ff4040" : "#e88020";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(pvp.aggressor ? "\u2694 AGGRESSOR" : "\u2694 PvP", bx + bw / 2, by + bh / 2);

  // Cooldown timer below badge
  const remaining = Math.max(0, pvp.cooldownUntil - now);
  if (remaining > 0) {
    ctx.font = "8px monospace";
    ctx.fillStyle = C.textDim;
    ctx.textBaseline = "top";
    const mins = Math.floor(remaining / 60);
    const secs = Math.floor(remaining % 60);
    ctx.fillText(`${mins}:${String(secs).padStart(2, "0")}`, bx + bw / 2, by + bh + 2);
  }

  ctx.restore();
}

// ─── PvP Honor Stats Panel ───────────────────────────────────────────────────
// Toggled with H key — shows honor rank, kills, deaths, K/D

const HONOR_RANK_NAMES = [
  "Unranked", "Private", "Corporal", "Sergeant", "Master Sergeant",
  "Sergeant Major", "Knight", "Knight-Lieutenant", "Knight-Captain",
  "Knight-Champion", "Lieutenant Commander", "Commander", "Marshal",
  "Field Marshal", "Grand Marshal",
];

function drawPvPStatsPanel(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  if (!state.ui.pvpStatsOpen) return;

  const pvp = state.player.pvp;
  const cw = state.canvasWidth;
  const PS_W = 200;
  const PS_H = 140;
  const px = (cw - PS_W) / 2;
  const py = 90;

  ctx.save();
  ctx.globalAlpha = 0.92;

  // Panel
  roundRect(ctx, px, py, PS_W, PS_H, 6);
  ctx.fillStyle = C.woodDark;
  ctx.fill();
  roundRect(ctx, px, py, PS_W, PS_H, 6);
  ctx.strokeStyle = "#e88020";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Title
  ctx.font = "bold 11px monospace";
  ctx.fillStyle = "#e88020";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("\u2694 PvP Honor \u2694", px + PS_W / 2, py + 6);

  // Separator
  ctx.strokeStyle = "rgba(232,128,32,0.3)";
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(px + 10, py + 22);
  ctx.lineTo(px + PS_W - 10, py + 22);
  ctx.stroke();

  // Rank name (golden, large)
  const rankName = HONOR_RANK_NAMES[pvp.honorRank] ?? "Unranked";
  ctx.font = "bold 12px monospace";
  ctx.fillStyle = pvp.honorRank >= 10 ? "#f0c860" : pvp.honorRank >= 5 ? "#d4a030" : "#c0a060";
  ctx.textAlign = "center";
  ctx.fillText(rankName, px + PS_W / 2, py + 28);

  // Rank bar (progress to next rank)
  const HONOR_RANKS = [0, 15, 50, 120, 250, 500, 1000, 2000, 3500, 5500, 8000, 12000, 17000, 23000, 30000];
  const currentThreshold = HONOR_RANKS[pvp.honorRank] ?? 0;
  const nextThreshold = HONOR_RANKS[pvp.honorRank + 1] ?? currentThreshold;
  const effectiveHonor = Math.max(0, pvp.honor - pvp.dishonor * 2);
  const rankProgress = nextThreshold > currentThreshold
    ? clamp((effectiveHonor - currentThreshold) / (nextThreshold - currentThreshold), 0, 1) : 1;
  const rbx = px + 20;
  const rby = py + 46;
  const rbw = PS_W - 40;
  const rbh = 8;
  roundRect(ctx, rbx, rby, rbw, rbh, 3);
  ctx.fillStyle = "#0e0a04";
  ctx.fill();
  const rfill = Math.round(rbw * rankProgress);
  if (rfill > 0) {
    ctx.save();
    roundRect(ctx, rbx, rby, rbw, rbh, 3);
    ctx.clip();
    ctx.fillStyle = "#e88020";
    ctx.fillRect(rbx, rby, rfill, rbh);
    ctx.restore();
  }
  roundRect(ctx, rbx, rby, rbw, rbh, 3);
  ctx.strokeStyle = C.hpBorder;
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // Stats rows
  const stats = [
    { label: "Honor", value: String(pvp.honor), color: "#e88020" },
    { label: "Dishonor", value: String(pvp.dishonor), color: pvp.dishonor > 0 ? "#e04040" : C.textDim },
    { label: "Kills", value: String(pvp.kills), color: C.textPrime },
    { label: "Deaths", value: String(pvp.deaths), color: C.textPrime },
    { label: "K/D", value: pvp.deaths > 0 ? (pvp.kills / pvp.deaths).toFixed(2) : pvp.kills > 0 ? "\u221e" : "0", color: C.textPrime },
  ];
  for (let i = 0; i < stats.length; i++) {
    const sy = py + 62 + i * 14;
    ctx.font = "8px monospace";
    ctx.fillStyle = C.textDim;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(stats[i].label, px + 14, sy);
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = stats[i].color;
    ctx.textAlign = "right";
    ctx.fillText(stats[i].value, px + PS_W - 14, sy);
  }

  // Close hint
  ctx.font = "7px monospace";
  ctx.fillStyle = C.textDim;
  ctx.textAlign = "center";
  ctx.fillText("[H] to close", px + PS_W / 2, py + PS_H - 10);

  ctx.restore();
}

// ─── Focus Target Frame ─────────────────────────────────────────────────────
// Smaller secondary target frame (set with F key) on the left side

function drawFocusTarget(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const ft = state.player.focusTarget;
  if (!ft.id) return;

  const FT_W = 160;
  const FT_H = 36;

  const fx = 12;
  const fy = state.canvasHeight / 2 - FT_H / 2 - 60;

  const age = now - ft.setAt;
  const fadeIn = clamp(age * 6, 0, 1);

  ctx.save();
  ctx.globalAlpha = fadeIn * 0.85;

  // Panel
  roundRect(ctx, fx, fy, FT_W, FT_H, 4);
  ctx.fillStyle = C.woodDark;
  ctx.fill();
  roundRect(ctx, fx, fy, FT_W, FT_H, 4);
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Disposition accent
  const dispColor = DISPOSITION_COLORS[ft.disposition] ?? DISPOSITION_COLORS.neutral;
  ctx.fillStyle = dispColor;
  ctx.fillRect(fx, fy, 3, FT_H);

  // Name
  ctx.font = "bold 9px monospace";
  ctx.fillStyle = dispColor;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const ftName = ft.level > 0 ? `[${ft.level}] ${ft.name}` : ft.name ?? "";
  ctx.fillText(ftName, fx + 8, fy + 4);

  // HP bar
  if (ft.maxHp > 0) {
    const bx = fx + 8;
    const by = fy + 18;
    const bw = FT_W - 16;
    const bh = 7;
    roundRect(ctx, bx, by, bw, bh, 2);
    ctx.fillStyle = C.hpBg;
    ctx.fill();
    const ratio = clamp(ft.hp / ft.maxHp, 0, 1);
    const fillW = Math.round(bw * ratio);
    if (fillW > 0) {
      ctx.save();
      roundRect(ctx, bx, by, bw, bh, 2);
      ctx.clip();
      ctx.fillStyle = hpColor(ratio);
      ctx.fillRect(bx, by, fillW, bh);
      ctx.restore();
    }
    roundRect(ctx, bx, by, bw, bh, 2);
    ctx.strokeStyle = C.hpBorder;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    ctx.font = "bold 6px monospace";
    ctx.fillStyle = C.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${ft.hp}/${ft.maxHp}`, bx + bw / 2, by + bh / 2);
  }

  // "Focus" label
  ctx.font = "7px monospace";
  ctx.fillStyle = C.textDim;
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText("FOCUS", fx + FT_W - 6, fy + 4);

  ctx.restore();
}

// ─── Combo Point Display ─────────────────────────────────────────────────────
// Ember rune dots below the target frame — visual combo counter

function drawComboPoints(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const combo = state.player.combatState.combo;
  if (combo <= 0 || !state.player.target.id) return;

  const cw = state.canvasWidth;
  const cx = cw / 2;
  const cy = TF_H + 16; // just below target frame

  const maxDots = 5;
  const dotR = 5;
  const dotGap = 14;
  const totalW = (maxDots - 1) * dotGap;
  const startX = cx - totalW / 2;

  ctx.save();
  for (let i = 0; i < maxDots; i++) {
    const dx = startX + i * dotGap;
    const filled = i < combo;

    // Outer ring
    ctx.beginPath();
    ctx.arc(dx, cy, dotR, 0, Math.PI * 2);
    ctx.fillStyle = filled ? "#1a0800" : "#0e0a04";
    ctx.fill();
    ctx.strokeStyle = filled ? "#e88020" : "#3a2810";
    ctx.lineWidth = 1;
    ctx.stroke();

    if (filled) {
      // Inner ember glow
      const pulse = 0.7 + Math.sin(now * 3 + i * 0.7) * 0.3;
      ctx.beginPath();
      ctx.arc(dx, cy, dotR - 2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(232, 128, 32, ${pulse})`;
      ctx.fill();

      // Bright center
      ctx.beginPath();
      ctx.arc(dx, cy, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 200, 100, ${pulse})`;
      ctx.fill();
    }
  }
  ctx.restore();
}

// ─── Party Frames ────────────────────────────────────────────────────────────
// Left-side column of party member HP bars, WoW-style

function drawPartyFrames(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const party = state.player.party;
  if (party.members.length === 0) return;

  const PF_W = 130;
  const PF_H = 32;
  const PF_GAP = 4;
  const startX = 8;
  const startY = 80;

  ctx.save();

  for (let i = 0; i < party.members.length; i++) {
    const m = party.members[i];
    const fx = startX;
    const fy = startY + i * (PF_H + PF_GAP);

    // Panel
    roundRect(ctx, fx, fy, PF_W, PF_H, 4);
    ctx.fillStyle = m.isDead ? "#1a1020" : C.woodDark;
    ctx.fill();

    // Aggro glow — red border pulse if this member has aggro
    if (m.hasAggro) {
      const aggroPulse = 0.5 + Math.sin(now * 5) * 0.5;
      ctx.save();
      ctx.strokeStyle = `rgba(224, 64, 64, ${aggroPulse})`;
      ctx.lineWidth = 2;
      ctx.shadowColor = "#e04040";
      ctx.shadowBlur = 4;
      roundRect(ctx, fx, fy, PF_W, PF_H, 4);
      ctx.stroke();
      ctx.restore();
    } else {
      roundRect(ctx, fx, fy, PF_W, PF_H, 4);
      ctx.strokeStyle = C.goldDark;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // Name
    ctx.font = "bold 8px monospace";
    ctx.fillStyle = m.isDead ? "#806060" : C.textPrime;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(m.name, fx + 6, fy + 3);

    // Marker icon next to name
    if (m.marker) {
      const markerGlyphs: Record<string, string> = {
        skull: "\u2620", cross: "\u2720", star: "\u2605", diamond: "\u2666",
        moon: "\u263D", triangle: "\u25B2", circle: "\u25CF", square: "\u25A0",
      };
      ctx.font = "8px serif";
      ctx.fillText(markerGlyphs[m.marker] ?? "", fx + PF_W - 14, fy + 3);
    }

    // HP bar
    const bx = fx + 6;
    const by = fy + 16;
    const bw = PF_W - 12;
    const bh = 10;

    roundRect(ctx, bx, by, bw, bh, 2);
    ctx.fillStyle = C.hpBg;
    ctx.fill();

    const ratio = m.maxHp > 0 ? clamp(m.hp / m.maxHp, 0, 1) : 0;
    const fillW = Math.round(bw * ratio);
    if (fillW > 0 && !m.isDead) {
      ctx.save();
      roundRect(ctx, bx, by, bw, bh, 2);
      ctx.clip();
      ctx.fillStyle = hpColor(ratio);
      ctx.fillRect(bx, by, fillW, bh);
      ctx.restore();
    }

    roundRect(ctx, bx, by, bw, bh, 2);
    ctx.strokeStyle = C.hpBorder;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // HP text / DEAD text
    ctx.font = "bold 7px monospace";
    ctx.fillStyle = m.isDead ? "#e04040" : C.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(m.isDead ? "DEAD" : `${m.hp}/${m.maxHp}`, bx + bw / 2, by + bh / 2);
  }

  ctx.restore();
}

// ─── Death Recap Panel ───────────────────────────────────────────────────────
// Shows what killed you — damage breakdown panel when dead

function drawDeathRecap(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  _now: number,
): void {
  const recap = state.player.deathRecap;
  if (!recap.visible || recap.entries.length === 0) return;

  const DR_W = 240;
  const ROW_H = 18;
  const headerH = 24;
  const DR_H = headerH + recap.entries.length * ROW_H + 8;
  const cw = state.canvasWidth;
  const ch = state.canvasHeight;
  const dx = (cw - DR_W) / 2;
  const dy = (ch - DR_H) / 2 - 40;

  ctx.save();
  ctx.globalAlpha = 0.92;

  // Panel
  roundRect(ctx, dx, dy, DR_W, DR_H, 6);
  ctx.fillStyle = "#1a0808";
  ctx.fill();
  roundRect(ctx, dx, dy, DR_W, DR_H, 6);
  ctx.strokeStyle = "#a04040";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Header
  ctx.font = "bold 10px monospace";
  ctx.fillStyle = "#e04040";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("DEATH RECAP", dx + DR_W / 2, dy + 6);

  // Separator
  ctx.strokeStyle = "rgba(160,64,64,0.4)";
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(dx + 10, dy + headerH);
  ctx.lineTo(dx + DR_W - 10, dy + headerH);
  ctx.stroke();

  // Rows — most recent damage first
  const sorted = [...recap.entries].sort((a, b) => b.timestamp - a.timestamp).slice(0, 8);
  for (let i = 0; i < sorted.length; i++) {
    const entry = sorted[i];
    const ry = dy + headerH + 4 + i * ROW_H;

    // Source name
    ctx.font = "8px monospace";
    ctx.fillStyle = "#c0a080";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(entry.sourceName, dx + 10, ry);

    // Ability
    ctx.fillStyle = "#a08060";
    ctx.textAlign = "center";
    ctx.fillText(entry.abilityName, dx + DR_W / 2, ry);

    // Damage
    ctx.font = "bold 8px monospace";
    ctx.fillStyle = entry.isCrit ? "#ff6040" : "#e04040";
    ctx.textAlign = "right";
    ctx.fillText(`-${entry.damage}${entry.isCrit ? "!" : ""}`, dx + DR_W - 10, ry);
  }

  // Total damage
  const total = sorted.reduce((s, e) => s + e.damage, 0);
  ctx.font = "bold 9px monospace";
  ctx.fillStyle = "#e08080";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(`Total: ${total} damage  [D] to close`, dx + DR_W / 2, dy + DR_H - 14);

  ctx.restore();
}

// ─── Ghost/Death Overlay ────────────────────────────────────────────────────
// When dead: full-screen desaturation + ghost UI with respawn button hint

function drawDeathOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const death = state.player.death;
  if (!death.isDead) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;

  // Desaturation overlay — the world becomes washed out
  ctx.save();
  const desatAlpha = 0.35;
  ctx.globalAlpha = desatAlpha;
  ctx.fillStyle = "#404050";
  ctx.fillRect(0, 0, cw, ch);
  ctx.restore();

  // Ethereal edge glow — ghostly blue-white vignette
  ctx.save();
  const cx = cw / 2;
  const cy = ch / 2;
  const outerR = Math.sqrt(cx * cx + cy * cy);
  const pulse = 0.1 + Math.sin((now - death.diedAt) * 1.5) * 0.04;

  const grad = ctx.createRadialGradient(cx, cy, outerR * 0.3, cx, cy, outerR);
  grad.addColorStop(0, "rgba(100, 120, 180, 0)");
  grad.addColorStop(0.7, `rgba(100, 120, 180, ${pulse * 0.5})`);
  grad.addColorStop(1, `rgba(60, 70, 120, ${pulse})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, cw, ch);
  ctx.restore();

  // ── "You Have Fallen" title ─────────────────────────────────────────
  const age = now - death.diedAt;
  const titleAlpha = age < 1 ? age : age < 3 ? 1 : Math.max(0.4, 1 - (age - 3) * 0.1);
  const titleScale = age < 0.3 ? 0.8 + age / 0.3 * 0.4 : 1.2 - Math.min(0.2, (age - 0.3) * 0.4);

  ctx.save();
  ctx.globalAlpha = clamp(titleAlpha, 0, 1);
  ctx.translate(cx, cy - 60);
  ctx.scale(clamp(titleScale, 0.8, 1.2), clamp(titleScale, 0.8, 1.2));

  ctx.font = "bold 24px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.fillText("You Have Fallen", 1, 1);

  // Ghost-blue text with glow
  ctx.shadowColor = "rgba(130, 160, 220, 0.6)";
  ctx.shadowBlur = 12;
  ctx.fillStyle = "#b0c4e8";
  ctx.fillText("You Have Fallen", 0, 0);
  ctx.fillText("You Have Fallen", 0, 0); // double for stronger glow

  ctx.restore();

  // ── XP penalty message ──────────────────────────────────────────────
  if (death.xpLost > 0 && age < 5) {
    ctx.save();
    ctx.globalAlpha = clamp(1 - (age - 3) / 2, 0.3, 1);
    ctx.font = "12px monospace";
    ctx.fillStyle = "#c08080";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`-${death.xpLost} XP`, cx, cy - 25);
    ctx.restore();
  }

  // ── Respawn prompt ──────────────────────────────────────────────────
  ctx.save();
  const promptY = cy + 20;

  if (death.canRespawn) {
    // "Press R to Respawn" with pulsing glow
    const respawnPulse = 0.7 + Math.sin(now * 3) * 0.3;
    ctx.globalAlpha = respawnPulse;
    ctx.font = "bold 14px monospace";
    ctx.fillStyle = "#e0d8b0";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(240, 216, 96, 0.4)";
    ctx.shadowBlur = 8;
    ctx.fillText("Press R to Respawn", cx, promptY);
  } else {
    // Countdown timer
    const remaining = Math.ceil(death.respawnTimer);
    ctx.font = "14px monospace";
    ctx.fillStyle = "#8090b0";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`Respawn in ${remaining}s...`, cx, promptY);

    // Progress bar below
    const barW = 160;
    const barH = 4;
    const barX = cx - barW / 2;
    const barY_ = promptY + 14;
    const totalTime = 10 + Math.min((death.deathCount - 1), 4) * 5; // match formula
    const progress = clamp(1 - death.respawnTimer / Math.max(1, totalTime), 0, 1);

    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fillRect(barX, barY_, barW, barH);
    ctx.fillStyle = "#8090b0";
    ctx.fillRect(barX, barY_, Math.round(barW * progress), barH);
  }

  // Ghost mode hint
  ctx.globalAlpha = 0.5;
  ctx.font = "10px monospace";
  ctx.fillStyle = "#8090b0";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("You are a spirit — move freely but cannot interact", cx, promptY + 36);

  ctx.restore();
}

// ─── Buff Tooltip ───────────────────────────────────────────────────────────

export function drawBuffTooltip(
  ctx: CanvasRenderingContext2D,
  name: string,
  description: string,
  duration: string,
  isDebuff: boolean,
  x: number,
  y: number,
): void {
  const PAD = 8;
  const LINE_H = 13;

  ctx.save();
  ctx.font = "bold 10px monospace";
  const nameW = ctx.measureText(name).width;
  ctx.font = "9px monospace";
  const descW = ctx.measureText(description).width;
  const durW = ctx.measureText(duration).width;

  const tipW = Math.max(nameW, descW, durW) + PAD * 2;
  const tipH = PAD * 2 + LINE_H * 3;

  // Position above the buff icon
  let tx = x - tipW / 2;
  const ty = y - tipH - 6;
  tx = Math.max(4, tx);

  // Shadow
  ctx.fillStyle = "rgba(10,6,0,0.5)";
  roundRect(ctx, tx + 2, ty + 2, tipW, tipH, 5);
  ctx.fill();

  // Background
  roundRect(ctx, tx, ty, tipW, tipH, 5);
  const grad = ctx.createLinearGradient(tx, ty, tx, ty + tipH);
  grad.addColorStop(0, "#2e2016");
  grad.addColorStop(1, "#1a1208");
  ctx.fillStyle = grad;
  ctx.fill();

  // Border — red for debuffs, gold for buffs
  roundRect(ctx, tx, ty, tipW, tipH, 5);
  ctx.strokeStyle = isDebuff ? "#a03020" : C.goldDark;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Name
  ctx.font = "bold 10px monospace";
  ctx.fillStyle = isDebuff ? "#e06050" : C.goldBright;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(name, tx + PAD, ty + PAD);

  // Description
  ctx.font = "9px monospace";
  ctx.fillStyle = C.textDim;
  ctx.fillText(description, tx + PAD, ty + PAD + LINE_H);

  // Duration
  ctx.fillStyle = "#a0855a";
  ctx.fillText(duration, tx + PAD, ty + PAD + LINE_H * 2);

  ctx.restore();
}

// ─── Target Selection Circle ───────────────────────────────────────────────
// A golden ellipse on the isometric ground plane under the targeted entity.
// Pulses gently (line width 1-2px at 3Hz). Uses disposition color with 0.5 alpha.

function drawTargetCircle(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  camera: Camera,
  now: number,
): void {
  const target = state.player.target;
  if (!target.id) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;

  // Find the target entity's position
  let tileX: number | null = null;
  let tileY: number | null = null;
  let elevation = 0;

  // Search creatures
  for (const creature of state.creatures) {
    if (creature.id === target.id) {
      tileX = creature.position.tileX;
      tileY = creature.position.tileY;
      elevation = creature.position.elevation;
      break;
    }
  }

  // Search NPCs
  if (tileX === null) {
    for (const npc of state.npcs) {
      if (npc.id === target.id) {
        tileX = npc.position.tileX;
        tileY = npc.position.tileY;
        elevation = npc.position.elevation;
        break;
      }
    }
  }

  // Search other players
  if (tileX === null) {
    for (const other of state.otherPlayers) {
      if (other.id === target.id) {
        tileX = other.position.tileX;
        tileY = other.position.tileY;
        elevation = other.position.elevation;
        break;
      }
    }
  }

  if (tileX === null || tileY === null) return;

  const screen = toScreen(tileX, tileY, elevation, camera, cw, ch);

  // Isometric ground-plane ellipse: wider than tall (2:1 ratio matching tile projection)
  const radiusX = 20 * camera.zoom;
  const radiusY = 10 * camera.zoom;

  // Pulsing line width at 3Hz
  const pulse = Math.sin(now * Math.PI * 6) * 0.5 + 1.5; // oscillates 1.0 - 2.0

  const dispColor = DISPOSITION_COLORS[target.disposition] ?? DISPOSITION_COLORS.neutral;

  ctx.save();
  ctx.globalAlpha = 0.5;
  ctx.strokeStyle = dispColor;
  ctx.lineWidth = pulse;

  ctx.beginPath();
  ctx.ellipse(screen.x, screen.y, radiusX, radiusY, 0, 0, Math.PI * 2);
  ctx.stroke();

  // Subtle inner fill for ground highlight
  ctx.globalAlpha = 0.08;
  ctx.fillStyle = dispColor;
  ctx.fill();

  ctx.restore();
}

// ─── Combat Log ────────────────────────────────────────────────────────────
// Small scrolling text area in the bottom-left showing the last combat events.
// Each entry is color-coded and fades out over 8 seconds.

interface CombatLogEntry {
  text: string;
  color: string;
  time: number; // performance.now() / 1000
}

const combatLog: CombatLogEntry[] = [];
const MAX_LOG_ENTRIES = 8;
const LOG_ENTRY_LIFETIME = 8; // seconds

export function addCombatLogEntry(text: string, color: string): void {
  combatLog.push({ text, color, time: performance.now() / 1000 });
  if (combatLog.length > MAX_LOG_ENTRIES) combatLog.shift();
}

function drawCombatLog(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  // Clean expired entries
  for (let i = combatLog.length - 1; i >= 0; i--) {
    if (now - combatLog[i].time >= LOG_ENTRY_LIFETIME) {
      combatLog.splice(i, 1);
    }
  }

  if (combatLog.length === 0) return;

  const lineH = 12;
  const padX = 6;
  const padY = 4;
  const maxVisible = 6;

  // Position: bottom-left, above skill bar area
  const baseX = 12;
  const baseY = state.canvasHeight - 80;

  // Draw only the last `maxVisible` entries
  const startIdx = Math.max(0, combatLog.length - maxVisible);
  const visible = combatLog.slice(startIdx);

  // Semi-transparent background strip
  const stripH = visible.length * lineH + padY * 2;
  const stripY = baseY - stripH;

  ctx.save();

  // Measure widest line for background width
  ctx.font = "8px monospace";
  let maxW = 0;
  for (const entry of visible) {
    const relTime = now - entry.time;
    const relLabel = relTime < 1 ? "now" : `${Math.floor(relTime)}s ago`;
    const fullText = `[${relLabel}] ${entry.text}`;
    const w = ctx.measureText(fullText).width;
    if (w > maxW) maxW = w;
  }
  const stripW = maxW + padX * 2 + 4;

  // Background
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = "#0a0604";
  roundRect(ctx, baseX, stripY, stripW, stripH, 3);
  ctx.fill();
  ctx.globalAlpha = 1;

  // Draw each entry
  ctx.font = "8px monospace";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  for (let i = 0; i < visible.length; i++) {
    const entry = visible[i];
    const age = now - entry.time;
    // Fade out over the last 2 seconds of the 8-second lifetime
    const fadeStart = LOG_ENTRY_LIFETIME - 2;
    const alpha = age > fadeStart ? clamp(1 - (age - fadeStart) / 2, 0, 1) : 1;

    const relTime = age < 1 ? "now" : `${Math.floor(age)}s ago`;
    const lineY = stripY + padY + i * lineH;

    ctx.globalAlpha = alpha * 0.6;
    ctx.fillStyle = "#808080";
    ctx.fillText(`[${relTime}]`, baseX + padX, lineY);

    const bracketW = ctx.measureText(`[${relTime}] `).width;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = entry.color;
    ctx.fillText(entry.text, baseX + padX + bracketW, lineY);
  }

  ctx.restore();
}

// ─── Duel Challenge Prompt ─────────────────────────────────────────────────
// When another player challenges you, show a prominent notification with
// accept/decline keybinds. The prompt pulsates gently to demand attention.

function drawDuelChallengePrompt(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const challenge = state.player.duel.pendingChallenge;
  if (!challenge) return;

  // Auto-expire check
  if (now > challenge.expiresAt / 1000) return;

  const cw = state.canvasWidth;
  const promptW = 260;
  const promptH = 52;
  const promptX = (cw - promptW) / 2;
  const promptY = 70;

  // Subtle pulse
  const pulse = 1 + Math.sin(now * 4) * 0.02;

  ctx.save();
  ctx.translate(promptX + promptW / 2, promptY + promptH / 2);
  ctx.scale(pulse, pulse);
  ctx.translate(-(promptX + promptW / 2), -(promptY + promptH / 2));

  // Drop shadow
  ctx.fillStyle = "rgba(10,6,0,0.5)";
  roundRect(ctx, promptX + 2, promptY + 2, promptW, promptH, 6);
  ctx.fill();

  // Panel background
  roundRect(ctx, promptX, promptY, promptW, promptH, 6);
  const bgG = ctx.createLinearGradient(promptX, promptY, promptX, promptY + promptH);
  bgG.addColorStop(0, "#2e2016");
  bgG.addColorStop(1, "#1a1208");
  ctx.fillStyle = bgG;
  ctx.fill();

  // Gold border with pvp-orange tint
  roundRect(ctx, promptX, promptY, promptW, promptH, 6);
  ctx.strokeStyle = "#e88020";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Challenge text
  ctx.font = "bold 10px monospace";
  ctx.fillStyle = "#e88020";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(`${challenge.challengerName} challenges you!`, promptX + promptW / 2, promptY + 16);

  // Keybind hint
  ctx.font = "9px monospace";
  ctx.fillStyle = C.textPrime;
  ctx.fillText("[Y] Accept    [N] Decline", promptX + promptW / 2, promptY + 35);

  // Countdown bar showing time remaining
  const total = (challenge.expiresAt - challenge.receivedAt) / 1000;
  const remaining = Math.max(0, challenge.expiresAt / 1000 - now);
  const ratio = clamp(remaining / total, 0, 1);
  const barY = promptY + promptH - 4;
  const barW = Math.round((promptW - 8) * ratio);
  ctx.fillStyle = "rgba(232, 128, 32, 0.3)";
  ctx.fillRect(promptX + 4, barY, barW, 2);

  ctx.restore();
}

// ─── Duel Overlay ──────────────────────────────────────────────────────────
// Full-screen duel UI: countdown, active duel banner, and result display.

function drawDuelOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const duel = state.player.duel;
  if (!duel.active && !duel.winnerId) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;
  const cx = cw / 2;
  const cy = ch / 2;

  ctx.save();

  // ── Countdown phase: 3... 2... 1... ──────────────────────────────
  if (duel.countdown > 0 && duel.countdownStart > 0) {
    const elapsed = now - duel.countdownStart;
    // Each number lasts 1 second
    const currentNum = Math.ceil(3 - elapsed);

    if (currentNum >= 1 && currentNum <= 3) {
      const numAge = elapsed - (3 - currentNum); // age within this number's second
      // Scale-pop: starts at 2.0x, settles to 1.0x over 0.3s, then holds
      const popT = clamp(numAge / 0.3, 0, 1);
      const scale = 2.0 - popT * 1.0; // 2.0 → 1.0
      // Fade out in the last 0.2s of each second
      const fadeOut = numAge > 0.8 ? 1 - (numAge - 0.8) / 0.2 : 1;
      const alpha = clamp(fadeOut, 0, 1);

      ctx.globalAlpha = alpha;
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);

      // Glow
      ctx.shadowColor = "rgba(240, 200, 96, 0.7)";
      ctx.shadowBlur = 24;

      ctx.font = "bold 72px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Dark outline
      ctx.strokeStyle = "rgba(0,0,0,0.8)";
      ctx.lineWidth = 6;
      ctx.lineJoin = "round";
      ctx.strokeText(`${currentNum}`, 0, 0);

      // Gold fill
      ctx.fillStyle = C.goldGlow;
      ctx.fillText(`${currentNum}`, 0, 0);
      // Double fill for stronger glow
      ctx.fillText(`${currentNum}`, 0, 0);

      ctx.setTransform(1, 0, 0, 1, 0, 0); // reset transform
    }
  }

  // ── Duel zone boundary ring ─────────────────────────────────────
  // Pulsing golden ring on the ground around the player during active duel
  if (duel.active && duel.countdown <= 0 && !duel.winnerId) {
    const ringPulse = 0.3 + Math.sin(now * 2) * 0.15;
    ctx.save();
    ctx.globalAlpha = ringPulse;
    ctx.strokeStyle = C.goldBright;
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.arc(cx, cy + 40, 80, 0, Math.PI * 2);
    ctx.stroke();
    // Outer glow ring
    ctx.globalAlpha = ringPulse * 0.4;
    ctx.strokeStyle = C.goldGlow;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy + 40, 90, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  // ── Active duel phase ────────────────────────────────────────────
  if (duel.countdown <= 0 && duel.startedAt > 0 && !duel.winnerId) {
    // "DUEL" banner at top
    const bannerY = 48;
    const bannerW = 280;
    const bannerH = 36;
    const bannerX = cx - bannerW / 2;

    // Panel background
    roundRect(ctx, bannerX, bannerY, bannerW, bannerH, 6);
    ctx.fillStyle = C.woodDark;
    ctx.fill();

    // Gold border
    roundRect(ctx, bannerX, bannerY, bannerW, bannerH, 6);
    ctx.strokeStyle = C.goldDark;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Pulsing glow on border
    const duelPulse = 0.6 + Math.sin(now * 4) * 0.4;
    ctx.globalAlpha = duelPulse * 0.3;
    roundRect(ctx, bannerX, bannerY, bannerW, bannerH, 6);
    ctx.strokeStyle = C.goldGlow;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.globalAlpha = 1;

    // "DUEL" text
    ctx.font = "bold 14px monospace";
    ctx.fillStyle = C.goldGlow;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("DUEL", cx, bannerY + 12);

    // Opponent name below
    if (duel.opponentName) {
      ctx.font = "10px monospace";
      ctx.fillStyle = C.textDim;
      ctx.fillText(`vs ${duel.opponentName}`, cx, bannerY + 26);
    }

    // ── Side-by-side HP bars ──────────────────────────────────────
    const barW = 100;
    const barH = 8;
    const barGap = 20;
    const barsY = bannerY + bannerH + 6;

    // Player HP bar (left side)
    const playerHpRatio = state.player.combat.maxHp > 0
      ? clamp(state.player.combat.hp / state.player.combat.maxHp, 0, 1)
      : 0;
    const pBarX = cx - barGap / 2 - barW;

    ctx.fillStyle = C.hpBg;
    roundRect(ctx, pBarX, barsY, barW, barH, 2);
    ctx.fill();

    const pFillW = Math.round(barW * playerHpRatio);
    if (pFillW > 0) {
      ctx.save();
      roundRect(ctx, pBarX, barsY, barW, barH, 2);
      ctx.clip();
      ctx.fillStyle = hpColor(playerHpRatio);
      ctx.fillRect(pBarX, barsY, pFillW, barH);
      ctx.restore();
    }

    roundRect(ctx, pBarX, barsY, barW, barH, 2);
    ctx.strokeStyle = C.hpBorder;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Player name label
    ctx.font = "8px monospace";
    ctx.fillStyle = C.textPrime;
    ctx.textAlign = "right";
    ctx.textBaseline = "top";
    ctx.fillText(state.player.name, pBarX + barW, barsY + barH + 2);

    // Opponent HP bar (right side) — uses target frame HP as proxy
    const oBarX = cx + barGap / 2;
    const opponentTarget = state.player.target;
    const oppHpRatio = opponentTarget.id === duel.opponentId && opponentTarget.maxHp > 0
      ? clamp(opponentTarget.hp / opponentTarget.maxHp, 0, 1)
      : 1;

    ctx.fillStyle = C.hpBg;
    roundRect(ctx, oBarX, barsY, barW, barH, 2);
    ctx.fill();

    const oFillW = Math.round(barW * oppHpRatio);
    if (oFillW > 0) {
      ctx.save();
      roundRect(ctx, oBarX, barsY, barW, barH, 2);
      ctx.clip();
      ctx.fillStyle = hpColor(oppHpRatio);
      ctx.fillRect(oBarX, barsY, oFillW, barH);
      ctx.restore();
    }

    roundRect(ctx, oBarX, barsY, barW, barH, 2);
    ctx.strokeStyle = C.hpBorder;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Opponent name label
    ctx.font = "8px monospace";
    ctx.fillStyle = C.textPrime;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(duel.opponentName ?? "Opponent", oBarX, barsY + barH + 2);
  }

  // ── Duel result phase: VICTORY or DEFEAT ────────────────────────
  if (duel.winnerId) {
    const isVictory = duel.winnerId === state.player.id;
    const resultText = isVictory ? "VICTORY" : "DEFEAT";
    const resultColor = isVictory ? C.goldGlow : "#e04040";
    const glowColor = isVictory ? "rgba(240, 200, 96, 0.7)" : "rgba(224, 64, 64, 0.5)";

    // Fade out after 3 seconds from duel end — estimate using startedAt as reference
    // (winnerId is set when duel ends; we use a heuristic based on how long ago it ended)
    const timeSinceWin = duel.startedAt > 0 ? now - duel.startedAt : 0;
    // The result shows for 3 seconds then fades over 1 second
    const resultAge = timeSinceWin;
    const resultAlpha = resultAge > 3 ? clamp(1 - (resultAge - 3) / 1, 0, 1) : 1;

    if (resultAlpha > 0.01) {
      ctx.globalAlpha = resultAlpha;

      // Scale pop on entrance
      const popAge = Math.min(resultAge, 0.4);
      const popScale = popAge < 0.2 ? 1.0 + (1 - popAge / 0.2) * 0.5 : 1.0;

      ctx.translate(cx, cy - 30);
      ctx.scale(popScale, popScale);

      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 20;

      ctx.font = "bold 36px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Dark outline
      ctx.strokeStyle = "rgba(0,0,0,0.8)";
      ctx.lineWidth = 5;
      ctx.lineJoin = "round";
      ctx.strokeText(resultText, 0, 0);

      // Colored fill
      ctx.fillStyle = resultColor;
      ctx.fillText(resultText, 0, 0);
      ctx.fillText(resultText, 0, 0); // double for glow

      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
  }

  ctx.restore();
}

// ─── Context Menu Renderer ─────────────────────────────────────────────────
// Right-click context menu with wood panel background, gold border,
// hover highlight, disabled dimming, and auto-sizing.

function drawContextMenu(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const menu = state.contextMenu;
  if (!menu.open || menu.options.length === 0) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;

  const padX = 10;
  const padY = 6;
  const rowH = 22;
  const iconW = 16;
  const menuR = 5;

  ctx.save();

  // Measure widest option label to auto-size width
  ctx.font = "10px monospace";
  let maxLabelW = 0;
  for (const opt of menu.options) {
    const w = ctx.measureText(opt.label).width;
    if (w > maxLabelW) maxLabelW = w;
  }

  const menuW = padX * 2 + iconW + maxLabelW + 8;
  const menuH = padY * 2 + menu.options.length * rowH;

  // Clamp position to screen edges
  let mx = menu.x;
  let my = menu.y;
  if (mx + menuW > cw - 4) mx = cw - 4 - menuW;
  if (my + menuH > ch - 4) my = ch - 4 - menuH;
  if (mx < 4) mx = 4;
  if (my < 4) my = 4;

  // Drop shadow
  ctx.fillStyle = "rgba(10, 6, 0, 0.4)";
  roundRect(ctx, mx + 3, my + 3, menuW, menuH, menuR);
  ctx.fill();

  // Wood panel background
  roundRect(ctx, mx, my, menuW, menuH, menuR);
  const bgGrad = ctx.createLinearGradient(mx, my, mx, my + menuH);
  bgGrad.addColorStop(0, "#2a1d0e");
  bgGrad.addColorStop(0.5, "#1e1508");
  bgGrad.addColorStop(1, "#1a1208");
  ctx.fillStyle = bgGrad;
  ctx.fill();

  // Inner highlight along top edge
  ctx.save();
  roundRect(ctx, mx, my, menuW, menuH, menuR);
  ctx.clip();
  const innerG = ctx.createLinearGradient(mx, my, mx, my + 6);
  innerG.addColorStop(0, "rgba(52,36,20,0.5)");
  innerG.addColorStop(1, "transparent");
  ctx.fillStyle = innerG;
  ctx.fillRect(mx, my, menuW, 6);
  ctx.restore();

  // Gold border
  roundRect(ctx, mx, my, menuW, menuH, menuR);
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Target name header (if present)
  if (menu.targetName) {
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = C.goldBright;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    // Name is rendered as part of the first row area
  }

  // Draw each option row
  const hoverIdx = menu.hoverIdx;
  for (let i = 0; i < menu.options.length; i++) {
    const opt = menu.options[i];
    const rowY = my + padY + i * rowH;

    const enabled = opt.enabled;
    const hovered = i === hoverIdx && enabled;

    // Hover highlight — warm amber bar
    if (hovered) {
      ctx.save();
      roundRect(ctx, mx + 2, rowY, menuW - 4, rowH, 3);
      ctx.fillStyle = "rgba(180, 140, 50, 0.18)";
      ctx.fill();
      ctx.restore();
    }

    // Icon
    ctx.save();
    ctx.globalAlpha = enabled ? 1 : 0.4;
    ctx.font = "11px monospace";
    ctx.fillStyle = hovered ? C.goldBright : (opt.color ?? C.textPrime);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(opt.icon, mx + padX + iconW / 2, rowY + rowH / 2);

    // Label
    ctx.font = "10px monospace";
    ctx.fillStyle = !enabled ? C.textDim : hovered ? C.goldBright : (opt.color ?? C.textPrime);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(opt.label, mx + padX + iconW + 4, rowY + rowH / 2);

    ctx.restore();

    // Separator line between options (except last)
    if (i < menu.options.length - 1) {
      ctx.strokeStyle = "rgba(106, 79, 12, 0.3)";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(mx + padX, rowY + rowH);
      ctx.lineTo(mx + menuW - padX, rowY + rowH);
      ctx.stroke();
    }
  }

  ctx.restore();
}

// ─── Main Export ─────────────────────────────────────────────────────────────

/**
 * Draw all nameplates, target frame, PvP badge, death overlay, duel overlay,
 * combat log, and context menu.
 */
export function drawNameplatesAndStatus(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  camera: Camera,
  now: number,
): void {
  const cw = state.canvasWidth;
  const ch = state.canvasHeight;
  const targetId = state.player.target.id;

  // ── Target selection circle (under nameplates) ──────────────────────
  drawTargetCircle(ctx, state, camera, now);

  // ── Creature nameplates ─────────────────────────────────────────────
  for (const creature of state.creatures) {
    const screen = toScreen(
      creature.position.tileX,
      creature.position.tileY,
      creature.position.elevation,
      camera, cw, ch,
    );

    // Skip if off screen
    if (screen.x < -100 || screen.x > cw + 100 || screen.y < -100 || screen.y > ch + 100) continue;

    const hp = getCreatureHp(creature.id);
    const creatureHp = hp?.hp ?? 0;
    const creatureMaxHp = hp?.maxHp ?? 0;
    const isTarget = creature.id === targetId;

    if (isTrainingDummy(creature)) {
      // Training dummies get specialized nameplates with tier colors
      const tier = creature.speciesId.replace("training_dummy_", "");
      const hits = state.trainingDummy?.hitsAbsorbed[creature.id] ?? 0;
      drawDummyNameplate(ctx, creature.name, tier, screen.x, screen.y - 30 * camera.zoom, hits, now);
      // Still show HP bar if in combat
      if (creatureMaxHp > 0 && creatureHp < creatureMaxHp) {
        drawCreatureNameplate(ctx, creature.name, creatureHp, creatureMaxHp, screen.x, screen.y, camera.zoom, isTarget, "hostile");
      }
    } else {
      drawCreatureNameplate(
        ctx,
        creature.name,
        creatureHp,
        creatureMaxHp,
        screen.x,
        screen.y,
        camera.zoom,
        isTarget,
        "hostile",
      );
    }

    // Threat glow — red outline when creature is chasing player
    const isChasing = creature.behaviorState.current === "chase";
    if (isChasing && creatureMaxHp > 0 && creatureHp > 0) {
      const barY = screen.y - 48 * camera.zoom;
      drawNameplateThreatGlow(ctx, screen.x, barY, 52, true);
    }

    // Raid marker — show if this creature is the target and has a marker
    if (isTarget && state.player.target.marker) {
      drawNameplateMarker(ctx, screen.x, screen.y, camera.zoom, state.player.target.marker);
    }
  }

  // ── NPC nameplates ──────────────────────────────────────────────────
  for (const npc of state.npcs) {
    const screen = toScreen(
      npc.position.tileX,
      npc.position.tileY,
      npc.position.elevation,
      camera, cw, ch,
    );

    if (screen.x < -100 || screen.x > cw + 100 || screen.y < -100 || screen.y > ch + 100) continue;

    const isTarget = npc.id === targetId;
    drawNpcNameplate(ctx, npc.name, npc.title, screen.x, screen.y, camera.zoom, isTarget);
  }

  // ── Other player nameplates ─────────────────────────────────────────
  for (const other of state.otherPlayers) {
    const screen = toScreen(
      other.position.tileX,
      other.position.tileY,
      other.position.elevation,
      camera, cw, ch,
    );

    if (screen.x < -100 || screen.x > cw + 100 || screen.y < -100 || screen.y > ch + 100) continue;

    const isTarget = other.id === targetId;
    const disposition = other.pvpAggressor ? "pvp_aggressor"
      : other.pvpFlagged && state.player.pvp.flagged ? "pvp_flagged"
      : other.pvpFlagged ? "attackable"
      : "friendly";

    drawPlayerNameplate(
      ctx,
      other.name,
      other.combat.level,
      disposition,
      screen.x,
      screen.y,
      camera.zoom,
      isTarget,
      other.isDead,
      other.pvpFlagged,
    );
  }

  // ── Target frame ────────────────────────────────────────────────────
  drawTargetFrame(ctx, state, now);

  // ── Combo points (below target frame) ──────────────────────────────
  drawComboPoints(ctx, state, now);

  // ── Focus target (left side) ───────────────────────────────────────
  drawFocusTarget(ctx, state, now);

  // ── Party frames (left column) ────────────────────────────────────
  drawPartyFrames(ctx, state, now);

  // ── PvP badge ───────────────────────────────────────────────────────
  drawPvPBadge(ctx, state, now);

  // ── PvP stats panel (H key) ──────────────────────────────────────
  drawPvPStatsPanel(ctx, state, now);

  // ── Death overlay ───────────────────────────────────────────────────
  drawDeathOverlay(ctx, state, now);

  // ── Death recap panel (when dead + D pressed) ─────────────────────
  drawDeathRecap(ctx, state, now);

  // ── Duel challenge prompt (before duel overlay) ────────────────────
  drawDuelChallengePrompt(ctx, state, now);

  // ── Duel overlay (after death overlay) ──────────────────────────────
  drawDuelOverlay(ctx, state, now);

  // ── Combat log (bottom-left) ────────────────────────────────────────
  drawCombatLog(ctx, state, now);

  // ── Context menu (on top of everything) ─────────────────────────────
  drawContextMenu(ctx, state, now);
}
